import { DocumentAttachment } from 'models';
import { createDataUrl } from 'src/app/common/utility';

/**
 * Converts RTF (Rich Text Format) string to clean HTML.
 * Handles RTF-embedded HTML tags and special formatting.
 *
 * @param rtfString The decompressed RTF string
 * @returns Clean HTML string
 */
export function convertRtfToHtml(rtfString: string): string {
  return (
    rtfString
      // Remove RTF header and control words (including \deff, \ansi, etc.)
      .replace(/^{\\rtf\d+\\[a-z]+(\d+)?[^{]*/g, '')
      // Remove font table section - match until the closing }} (nested structure closes)
      .replace(/{\\fonttbl[\s\S]*?}}\s*/g, '')
      // Remove color table section
      .replace(/{\\colortbl[\s\S]*?}/g, '')
      // Remove additional RTF control words that might appear after header
      .replace(/\\fromhtml\d+\s*/g, '')
      .replace(/\\fbidis\s*/g, '')
      .replace(/\\deff\d+\s*/g, '')
      // Remove stylesheet section
      .replace(
        /{\s*\\\*\\htmltag\d+\s+<style>[\s\S]*?{\s*\\\*\\htmltag\d+\s+<\/style>[\s\S]*?}/g,
        ''
      )
      // Remove meta tags
      .replace(/{\s*\\\*\\htmltag\d+\s+<meta[^>]*>[\s\S]*?}/g, '')
      // Remove \htmlrtf markers and RTF-only content (between \htmlrtf and \htmlrtf0) EARLY
      // This ensures we don't process RTF field instructions that are RTF-only
      .replace(/\\htmlrtf[\s\S]*?\\htmlrtf0/g, '')
      .replace(/\\htmlrtf\d?\s*/g, '')
      // Extract HTML from htmltag groups (these contain actual HTML)
      // Use a more robust pattern that handles anchor tags and other HTML properly
      .replace(/{\s*\\\*\\htmltag\d+\s+([^}]+)}/g, '$1')
      // Remove other RTF groups that aren't htmltags
      .replace(/{\s*\\\*\\(?!htmltag)[^}]*}/g, '')
      // Handle mso-list control sequences
      .replace(/\\[a-z]+-[a-z]+:[^;]*;/g, '')
      // Replace special characters
      .replace(/\\'([0-9a-fA-F]{2})/g, (_, hex) =>
        String.fromCharCode(Number.parseInt(hex, 16))
      )
      .replace(/\\'ae/g, '®')
      .replace(/\\'a0/g, '&nbsp;')
      // Convert RTF field hyperlinks to HTML before removing field instructions
      // Pattern handles: {\field{\*\fldinst{HYPERLINK "url"}}{\fldrslt...text...}}
      .replace(
        /\\field\s*{\s*\\\*\\fldinst\s*{\s*HYPERLINK\s+"([^"]+)"\s*}\s*}[\s\S]*?\\fldrslt\s*[^}]*}\s*/g,
        (match, url) => {
          // Extract the link text from the fldrslt section
          const textMatch = match.match(/\\fldrslt[\s\S]*?([^\\}]+)/);
          const linkText = textMatch ? textMatch[1].trim() : url;
          return `<a href="${url}">${linkText}</a>`;
        }
      )
      // Remove paragraph and text formatting control words (before converting \par and \line to newlines)
      .replace(/\\pard\s*/g, '')
      .replace(/\\plain\s*/g, '')
      .replace(/\\deftab\d+\s*/g, '')
      .replace(/\\uc\d+\s*/g, '')
      .replace(/\\lang\d+\s*/g, '')
      .replace(/\\langfe\d+\s*/g, '')
      // Remove font and formatting control words
      .replace(/\\f\d+\s*/g, '')
      .replace(/\\fs\d+\s*/g, '')
      .replace(/\\cf\d+\s*/g, '')
      .replace(/\\b\d?\s*/g, '')
      .replace(/\\i\d?\s*/g, '')
      .replace(/\\ul\d?\s*/g, '')
      .replace(/\\strike\d?\s*/g, '')
      // Replace line breaks and paragraph breaks (after removing \pard, \plain, etc.)
      .replace(/\\line\s*/g, '\n')
      .replace(/\\par\s*/g, '\n')
      .replace(/\\tab\s*/g, '\t')
      // Remove any remaining field instructions (after converting hyperlinks)
      .replace(/\\field[\s\S]*?\\fldrslt[\s\S]*?}}/g, '')
      // Remove remaining control words and sequences
      .replace(/\\[a-z]+\d*\s*/gi, '')
      // Clean up remaining curly braces
      .replace(/[{}]/g, '')
      // Fix anchor tags with relative URLs - ensure they have proper protocols
      .replace(
        /<a\s+href="((?!https?:\/\/|mailto:)[^"]+)"/gi,
        '<a href="http://$1"'
      )
      // Trim whitespace
      .trim()
  );
}

/**
 * Replaces CID (Content ID) image references with data URLs.
 * CID references are typically used in email bodies to embed images.
 *
 * @param body The HTML body containing CID references
 * @param attachments Array of attachments with name and byte content
 * @returns Modified HTML body with CID references replaced by data URLs
 */
export function replaceCidReferencesWithDataUrls(
  body: string,
  attachments: DocumentAttachment[]
): string {
  // Replace cid: references in img tags with data URLs from attachments
  if (body && attachments.length > 0) {
    // Find all cid: references in the HTML
    const cidPattern = /cid:([^"'\s>]+)/gi;
    let match: RegExpExecArray | null;
    while ((match = cidPattern.exec(body)) !== null) {
      const cidValue = match[1];
      // Extract filename from cid (usually format: filename@domain or just filename)
      const filename = cidValue.split('@')[0];

      // Find matching attachment by filename
      const attachment = attachments.find(
        (att) => att.name === filename || att.name.startsWith(filename)
      );

      if (attachment) {
        const dataUrl = createDataUrl(
          new Uint8Array(attachment.bytes),
          // Try to determine MIME type from extension
          attachment.name.match(/\.(png|jpe?g|gif|bmp|svg|webp)$/i)
            ? `image/${attachment.name.split('.').pop()?.toLowerCase().replace('jpg', 'jpeg')}`
            : 'application/octet-stream'
        );
        // Replace all occurrences of this cid reference
        body = body.replace(
          new RegExp(
            `cid:${cidValue.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}`,
            'gi'
          ),
          dataUrl
        );
      }
    }
  }

  return body;
}
